import {
  Alert,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
} from "@heroui/react";
import { Copy } from "lucide-react";
import React, { type MouseEvent,useCallback, useEffect, useRef } from "react";
import { toast } from "react-toastify";

import { useDialog } from "@/contexts/DialogContext";
import { useTemplateContext } from "@/contexts/TemplateContext";
import { CanvasCore } from "@/lib/canvas";
import { type ITemplateState,Template } from "@/lib/template";

import { Button } from "../core/Button";

export const ShareModal = () => {
  const Dialog = useDialog();
  const template = useTemplateContext();
  const canvas = useRef<CanvasCore>(null);

  useEffect(() => {
    canvas.current = CanvasCore.get(false);
  }, [Dialog.state.dialogs]);

  const url = useCallback((withTemplate: boolean) => {
    if (canvas.current) {
      return String(
        ShareURL(
          canvas.current?.panZoomTransformToCanvas(),
          withTemplate ? (Template.getState() ?? undefined) : undefined,
        ),
      );
    }
    return null;
  }, []);

  return (
    <Modal
      isOpen={"SHARE_MODAL" in Dialog.state.dialogs}
      onOpenChange={(open) =>
        open
          ? Dialog.dispatch({
              action: "OPEN_DIALOG",
              dialog: { id: "SHARE_MODAL" },
            })
          : Dialog.dispatch({ action: "CLOSE_DIALOG", dialogId: "SHARE_MODAL" })
      }
    >
      <ModalContent className="canvas-overlay-card">
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">Share</ModalHeader>
            <ModalBody>
              <p>Share your view of the canvas!</p>
              {canvas.current ? (
                <>
                  <CopyButton url={url(false)}>Just position</CopyButton>
                  <CopyButton isDisabled={!template.enable} url={url(true)}>
                    With template
                  </CopyButton>
                </>
              ) : (
                <Alert color="primary">
                  Unable to get canvas context, try refreshing
                </Alert>
              )}
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

const CopyButton = ({
  url,
  ...props
}: React.PropsWithChildren<Parameters<typeof Button>[0]> & {
  url: string | null;
}) => {
  const copy = useCallback(
    (e: MouseEvent) => {
      if (!url) return;
      if (e.ctrlKey || e.altKey || e.shiftKey) return;

      e.preventDefault();

      navigator.clipboard.writeText(url);
      toast.success("Copied to clipboard!");
    },
    [url],
  );

  return (
    <Button
      variant="flat"
      onClick={copy}
      startContent={<Copy aria-hidden="true" size={16} />}
      as={Link}
      href={url ?? undefined}
      {...props}
    >
      {props.children}
    </Button>
  );
};

const ShareURL = (
  {
    canvasX: x,
    canvasY: y,
    zoom,
  }: { canvasX: number; canvasY: number; zoom: number },
  template?: ITemplateState,
): URL => {
  let path = `/share/${x},${y}`;

  if (typeof zoom === "number") {
    path += `/z=${zoom}`;
  }

  if (template) {
    const params = new URLSearchParams();

    params.set("template", template.url);
    params.set("x", String(template.x));
    params.set("y", String(template.y));
    params.set("w", String(template.width));
    params.set("style", String(template.style));

    path += "?" + params;
  }

  return new URL(path, window.location.href);
};
